import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { Channel, ChannelFns } from 'ops/common/models/channel';
import { ChannelTypeStringLookup, ChannelTypeStrings } from 'ops/common/models/channel-type';
import {
    AlarmSettingValidationField,
    Schema,
    ValidationError,
    alarmSettingValidator,
    channelValidator,
    scaleErrorValues,
} from 'ops/common/validators';
import lodash from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { TREND_GRAPH_MAX_CHANNELS } from 'ops/common/validators/trend-graph/trend-graph-view-validator';
import { BubbleSelectorComponent } from '../bubble-selector/bubble-selector.component';
import { AlarmSetting, AlarmSettingFns } from 'ops/common/models/alarm-setting';
import { OpsAccordionComponent } from '../ops-accordion/ops-accordion.component';
import { AlarmSettingFormComponent } from '../alarm-setting-form/alarm-setting-form.component';

import { DeviceFlagsService } from '@service/device-flags.service';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { TrendGraphView, TrendGraphViewFns } from 'ops/common/models/trend-graph-view';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { Mimic } from 'ops/common/models/mimic';
import { AuthService } from '@service/auth.service';
import { ScaledNumber } from 'src/app/directives/scaled-numeric-input/scaled-numeric-input.directive';
import { ElementSettings } from 'ops/common/models/editor-viewer-element';
import { MimicService } from '@service/mimic.service';

export interface ChannelAdvancedSettingSubmission {
    channel: Channel;
    modifiedGraphs: TrendGraphView[];
}

@Component({
    selector: 'app-channel-advanced-settings-form',
    templateUrl: './channel-advanced-settings-form.component.html',
    styleUrls: ['channel-advanced-settings-form.component.scss'],
    providers: [],
    standalone: false,
})
export class ChannelAdvancedSettingsFormComponent implements OnChanges, OnInit, AfterViewInit {
    MIN_FU_NO = 1;
    MAX_FU_NO = 20;
    MIN_SLOT_NO = 1;
    MAX_SLOT_NO = 8;
    MIN_PIN_NO = 1;
    MAX_PIN_NO = 64;

    REMOVE_FROM_GRAPH_PROMPT_MAIN_LABEL =
        'forms.channel.advanced.prompts.remove-trend-graph.main-label';
    REMOVE_FROM_GRAPH_PROMPT_CONFIRM_LABEL =
        'forms.channel.advanced.prompts.remove-trend-graph.confirm';
    REMOVE_FROM_GRAPH_PROMPT_CANCEL_LABEL =
        'forms.channel.advanced.prompts.remove-trend-graph.cancel';

    @ViewChild('graphBubbleSelector') graphBubbleSelector: BubbleSelectorComponent;

    @ViewChild('digitalAccordion') digitalAccordion: OpsAccordionComponent;
    @ViewChild('veryHighAccordion') veryHighAccordion: OpsAccordionComponent;
    @ViewChild('highAccordion') highAccordion: OpsAccordionComponent;
    @ViewChild('lowAccordion') lowAccordion: OpsAccordionComponent;
    @ViewChild('veryLowAccordion') veryLowAccordion: OpsAccordionComponent;
    @ViewChild('feedbackErrorAccordion') feedbackErrorAccordion: OpsAccordionComponent;

    @ViewChildren('alarmForms') alarmForms: QueryList<AlarmSettingFormComponent>;

    @ViewChild('notification', { static: true }) notification: PopUpNotificationComponent;

    @Output() onExit = new EventEmitter<void>();
    @Output() onSave = new EventEmitter<ChannelAdvancedSettingSubmission>();
    @Output() onCreateGraph = new EventEmitter<TrendGraphView>();

    @Input() channel: Channel;
    @Input() groupName: string;
    editedChannel: Channel;

    isInactiveGraphsVisible = false;

    elementSettings: ElementSettings[];
    vesselMimics: Mimic[];
    allGraphs: TrendGraphView[] = [];

    selectedGraphs: TrendGraphView[] = [];

    activeGraphs: TrendGraphView[] = [];
    inactiveGraphs: TrendGraphView[] = [];

    originalActiveGraphIds = new Set<string>();
    originalInactiveGraphIds = new Set<string>();

    validator: Schema;
    errorMap = {};
    errorCount = 0;
    isViewInit = false;
    isInit = false;

    mimics: Mimic[] = [];

    fullGraphErrors: { graphId: string; errorString: string }[] = [];

    channelTypeLabels: string[];
    typeValueLookup;
    userPermission: boolean = false;

    alarmValidators = {
        veryLow: null,
        low: null,
        high: null,
        veryHigh: null,
        feedback: null,
        sensorFail: null,
        alarmSetting: null,
    };

    private disabledAlarmFormFields = ['alarmLevel'];
    private disabledFeedbackAlarmFormFields = ['alarmLevel', 'setPoint'];

    constructor(
        private flagsService: DeviceFlagsService,
        private graphViewsService: TrendGraphViewsService,
        private confirmationPromptService: ConfirmationPromptService,
        private translate: TranslateService,
        private authService: AuthService,
        private mimicService: MimicService,
    ) {
        this.channelTypeLabels = Object.values(ChannelTypeStrings);
        this.typeValueLookup = ChannelTypeStringLookup;

        this.validator = channelValidator();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.channel && this.channel) {
            this.editedChannel = JSON.parse(JSON.stringify(this.channel));
            this.trackOriginalGraphs(this.allGraphs);

            this.alarmValidators.veryLow = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_VERY_LOW,
            );
            this.alarmValidators.low = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_LOW,
            );
            this.alarmValidators.high = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_HIGH,
            );
            this.alarmValidators.veryHigh = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_VERY_HIGH,
            );
            this.alarmValidators.feedback = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR,
            );
            this.alarmValidators.sensorFail = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_SETTING_SENSOR_FAIL,
            );
            this.alarmValidators.alarmSetting = alarmSettingValidator(
                this.editedChannel,
                AlarmSettingValidationField.ALARM_SETTING,
            );

            const userId = this.authService.getCurrentUserId();
            this.userPermission = await this.authService.checkChannelSettingPermission(
                userId,
                this.channel.groupId,
            );
        }
    }

    async ngOnInit() {
        this.graphViewsService.getAll().subscribe((graphs) => {
            this.trackOriginalGraphs(graphs);
            this.allGraphs = graphs;
        });
        this.authService.currentUser$.subscribe(async (user) => {
            this.userPermission = await this.authService.checkChannelSettingPermission(
                user.userId,
                this.channel.groupId,
            );
        });
        this.vesselMimics = await this.mimicService.getAll();
        this.setMimics();
        this.isInit = true;
    }

    ngAfterViewInit(): void {
        this.isViewInit = true;
    }

    trackOriginalGraphs(graphs: TrendGraphView[]) {
        this.activeGraphs = [];
        this.inactiveGraphs = [];
        this.originalActiveGraphIds = new Set<string>();
        this.originalInactiveGraphIds = new Set<string>();

        for (let i = 0; i < graphs.length; i++) {
            if (this.isInGraph(this.channel, graphs[i])) {
                this.activeGraphs.push(graphs[i]);
                this.originalActiveGraphIds.add(graphs[i].id);
            } else {
                this.inactiveGraphs.push(graphs[i]);
                this.originalInactiveGraphIds.add(graphs[i].id);
            }
        }
    }

    isInGraph(channel: Channel, graph: TrendGraphView) {
        return (
            graph.channels.find((v) => v.fcuChannelId == channel.fcuData.fcuChannelId) !== undefined
        );
    }

    setMimics() {
        const mimicIdsSet = new Set();
        this.mimics = [];
        for (let i = 0; i < this.elementSettings?.length; i++) {
            if (this.isInMimic(this.channel, this.elementSettings[i])) {
                mimicIdsSet.add(this.elementSettings[i].mimicId);
            }
        }

        this.vesselMimics.forEach((mimic) => {
            if (mimicIdsSet.has(mimic.id)) {
                this.mimics.push(mimic);
            }
        });
    }

    isInMimic(channel: Channel, elementSetting: ElementSettings) {
        const channelTag = channel.fcuData.fcuChannelId.toString();
        return (
            channelTag === elementSetting?.flashingStatus?.channelTag ||
            channelTag === elementSetting?.disabledStatus?.channelTag ||
            channelTag === elementSetting?.channelTag ||
            channelTag === elementSetting?.valveSetting?.openChannel?.tag ||
            channelTag === elementSetting?.valveSetting?.closeChannel?.tag ||
            channelTag === elementSetting?.valveSetting?.flashChannel?.tag
        );
    }

    hasUnsavedChanges(): boolean {
        return (
            !lodash?.isEqual(this.channel, this.editedChannel) ||
            this.getModifiedGraphs().length != 0
        );
    }

    handleAddGraphsClicked() {
        const removeGraphs = new Set(this.selectedGraphs);

        //Move the selected items from inactive to active
        this.inactiveGraphs = this.inactiveGraphs.filter((v) => !removeGraphs.has(v));
        this.activeGraphs.push(...this.selectedGraphs);

        for (let i = 0; i < this.selectedGraphs.length; i++) {
            const graph = this.selectedGraphs[i];
            graph.channels.push({
                fcuChannelId: this.channel.fcuData.fcuChannelId,
                color: this.graphViewsService.getNextcolor(graph),
                name: this.channel.opsData.name,
                channelId: this.channel.id,
            });
        }

        this.selectedGraphs = [];
        this.isInactiveGraphsVisible = false;
        this.graphBubbleSelector.clearSelection();
    }

    handleGraphSelectionChanged(graphs: TrendGraphView[]) {
        this.selectedGraphs = graphs;

        const fullGraphErrors = [];

        for (let i = 0; i < this.selectedGraphs.length; i++) {
            const graph = this.selectedGraphs[i];

            if (this.selectedGraphs[i].channels.length == TREND_GRAPH_MAX_CHANNELS) {
                fullGraphErrors.push({
                    graphId: graph.id,
                    errorString: this.getFullGraphErrorString(graph),
                });
            }
        }

        this.fullGraphErrors = fullGraphErrors;
    }

    handleRemoveGraphClicked(graph: TrendGraphView) {
        this.confirmationPromptService.onOptionSelected().subscribe((confirm) => {
            if (confirm) {
                this.removeTrendGraph(graph);
            }
        });

        this.confirmationPromptService.showPrompt({
            mainLabel: this.translate.instant(this.REMOVE_FROM_GRAPH_PROMPT_MAIN_LABEL),
            confirmLabel: this.translate.instant(this.REMOVE_FROM_GRAPH_PROMPT_CONFIRM_LABEL),
            cancelLabel: this.translate.instant(this.REMOVE_FROM_GRAPH_PROMPT_CANCEL_LABEL),
        });
    }

    removeTrendGraph(graph: TrendGraphView) {
        this.activeGraphs.splice(this.activeGraphs.indexOf(graph), 1);
        this.inactiveGraphs.push(graph);

        const indexInGraph = graph.channels.findIndex(
            (v) => v.fcuChannelId == this.channel.fcuData.fcuChannelId,
        );

        if (indexInGraph != -1) {
            graph.channels.splice(indexInGraph, 1);
        }
    }

    getGraphLabel(view: TrendGraphView) {
        return view.name;
    }

    getDisplayValue() {
        return ChannelFns.transformValue(this.channel, this.getRawValue());
    }

    showInactiveGraphs() {
        this.isInactiveGraphsVisible = true;
    }

    getModifiedGraphs() {
        const modifiedGraphs: TrendGraphView[] = [];

        //Graphs added
        for (let i = 0; i < this.activeGraphs.length; i++) {
            if (!this.originalActiveGraphIds.has(this.activeGraphs[i].id)) {
                modifiedGraphs.push(this.activeGraphs[i]);
            }
        }

        //Graphs removed
        for (let i = 0; i < this.inactiveGraphs.length; i++) {
            if (!this.originalInactiveGraphIds.has(this.inactiveGraphs[i].id)) {
                modifiedGraphs.push(this.inactiveGraphs[i]);
            }
        }

        return modifiedGraphs;
    }

    handleInputChange() {
        this.updateErrors();
    }

    handleRangeMinChange(input: ScaledNumber) {
        this.updateScale(input, this.editedChannel.fcuData.measurableRange.min);
        this.editedChannel.fcuData.measurableRange.min = input.value;
        this.updateErrors();
    }

    handleRangeMaxChange(input: ScaledNumber) {
        this.updateScale(input, this.editedChannel.fcuData.measurableRange.max);
        this.editedChannel.fcuData.measurableRange.max = input.value;
        this.updateErrors();
    }

    updateScale(input: ScaledNumber, compareValue: number) {
        if (!Number.isNaN(input.value)) {
            const scaleChange = this.getScaleDifference(
                this.editedChannel.fcuData.scale,
                input.scale,
            );

            if (input.value != compareValue) {
                this.modifyAlarmsByScale(scaleChange);
            }

            this.editedChannel.fcuData.scale = input.scale;
        }
    }

    modifyAlarmsByScale(scaleChange: number) {
        const divisor = this.getScaleChangeDivisor(scaleChange);

        this.editedChannel.fcuData.measurableRange.min = Math.floor(
            this.editedChannel.fcuData.measurableRange.min / divisor,
        );
        this.editedChannel.fcuData.measurableRange.max = Math.floor(
            this.editedChannel.fcuData.measurableRange.max / divisor,
        );

        this.editedChannel.fcuData.normalRange.min = Math.floor(
            this.editedChannel.fcuData.normalRange.min / divisor,
        );
        this.editedChannel.fcuData.normalRange.max = Math.floor(
            this.editedChannel.fcuData.normalRange.max / divisor,
        );

        this.editedChannel.fcuData.alarmSettingVeryLow = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingVeryLow,
            divisor,
        );
        this.editedChannel.fcuData.alarmSettingLow = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingLow,
            divisor,
        );
        this.editedChannel.fcuData.alarmSettingHigh = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingHigh,
            divisor,
        );
        this.editedChannel.fcuData.alarmSettingVeryHigh = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingVeryHigh,
            divisor,
        );
        this.editedChannel.fcuData.alarmSettingFeedbackError = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingFeedbackError,
            divisor,
        );
        this.editedChannel.fcuData.alarmSettingSensorFail = this.modifyAlarmValue(
            this.editedChannel.fcuData.alarmSettingSensorFail,
            divisor,
        );
    }

    getScaleDifference(originalScale: number, newScale: number) {
        originalScale = Math.log10(originalScale);
        newScale = Math.log10(newScale);

        return Math.pow(10, newScale - originalScale);
    }

    getScaleChangeDivisor(scaleChange) {
        if (scaleChange == 0) {
            return 1;
        } else if (scaleChange > 0) {
            return 1 / scaleChange;
        } else {
            return Math.abs(scaleChange);
        }
    }

    modifyAlarmValue(alarm: AlarmSetting, divideBy: number): AlarmSetting {
        if (alarm && alarm.enable) {
            const clone = AlarmSettingFns.clone(alarm);
            clone.setPoint = Math.floor(clone.setPoint / divideBy);

            return clone;
        } else {
            return alarm;
        }
    }

    updateErrors() {
        this.setErrors(this.getValidationErrors(this.editedChannel));
        this.validateAlarmForms();
    }

    setErrors(errors: ValidationError[]) {
        this.errorMap = {};
        this.errorCount = errors.length;

        for (let i = 0; i < errors.length; i++) {
            this.errorMap[errors[i].path] = errors[i];
        }
    }

    doAlarmFormsHaveErrors() {
        return this.alarmForms.reduce(
            (hasErrors, form) => hasErrors || (form.alarm.enable && !form.isFormDataValid()),
            false,
        );
    }

    getValidationErrors(model: Channel) {
        try {
            this.validator.validateSync(model, { abortEarly: false });
            return [];
        } catch (err) {
            if (err.name == 'ValidationError') {
                scaleErrorValues(this.editedChannel, err);
                return err.inner;
            }

            throw err;
        }
    }

    validateAlarmForms() {
        for (const form of this.alarmForms) {
            form.validate();
        }
    }

    getErrorString(error: ValidationError): string {
        return error ? this.translate.instant(error.message, error.params) : '';
    }

    getMeasurableRangeMinErrorString() {
        return this.getErrorString(this.errorMap['fcuData.measurableRange.min']);
    }

    getMeasurableRangeMaxErrorString(): string {
        return this.getErrorString(this.errorMap['fcuData.measurableRange.max']);
    }

    getNormalRangeMinErrorString() {
        return this.getErrorString(this.errorMap['fcuData.normalRange.min']);
    }

    getNormalRangeMaxErrorString() {
        return this.getErrorString(this.errorMap['fcuData.normalRange.max']);
    }

    getFullGraphErrorString(graph: TrendGraphView) {
        return this.translate.instant('forms.channel.advanced.errors.trend-graph.full-graph', {
            name: graph.name,
        });
    }

    handleExitClicked() {
        this.emitExit();
    }

    emitExit() {
        this.onExit.emit();
    }

    handleSaveClicked() {
        this.emitSave();
    }

    emitSave() {
        const channel = JSON.parse(JSON.stringify(this.editedChannel));
        const graphs = JSON.parse(JSON.stringify(this.getModifiedGraphs()));

        this.onSave.emit({
            channel,
            modifiedGraphs: graphs,
        });
    }

    handleCreateGraphClicked() {
        this.emitCreateGraph();
    }

    emitCreateGraph() {
        const graph = TrendGraphViewFns.create();
        graph.channels.push({
            fcuChannelId: this.channel.fcuData.fcuChannelId,
            color: this.graphViewsService.getNextcolor(graph),
            name: this.channel.opsData.name,
            channelId: this.channel.id,
        });
        this.onCreateGraph.emit(graph);
    }

    handleAlarmSettingUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.digitalAlarmSetting = alarm;
        this.digitalAccordion.setOpen(false);
        this.updateErrors();
    }
    handleVeryHighAlarmUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.alarmSettingVeryHigh = alarm;
        this.veryHighAccordion.setOpen(false);
        this.updateErrors();
    }
    handleHighAlarmUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.alarmSettingHigh = alarm;
        this.highAccordion.setOpen(false);
        this.updateErrors();
    }
    handleLowAlarmUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.alarmSettingLow = alarm;
        this.lowAccordion.setOpen(false);
        this.updateErrors();
    }
    handleVeryLowAlarmUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.alarmSettingVeryLow = alarm;
        this.veryLowAccordion.setOpen(false);
        this.updateErrors();
    }
    handleFeedbacklarmUpdate(alarm: AlarmSetting) {
        this.editedChannel.fcuData.alarmSettingFeedbackError = alarm;
        this.feedbackErrorAccordion.setOpen(false);
        this.updateErrors();
    }

    shouldShowErrorBorder(form: AlarmSettingFormComponent) {
        return form && form.alarm && form.alarm.enable && !form.isFormDataValid();
    }

    onEditMimic() {
        this.notification.setVisible(true);
    }

    getRawValue = () => this.flagsService.getFlags(this.channel)?.value;
    isDigital = () => ChannelFns.isDigital(this.channel);
    shouldShowValueSlider = () => !this.isDigital();
    getChannelTag = () => this.channel.opsData.tag;
    getChannelName = () => this.channel.opsData.name;
    getCurrentUnits = () => this.channel.opsData.measurementUnit;

    getScale = () => this.editedChannel.fcuData.scale;
    getMeasurableRangeMin = () => {
        const scale = this.editedChannel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.editedChannel.fcuData.measurableRange.min / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    };
    getMeasurableRangeMax = () => {
        const scale = this.editedChannel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.editedChannel.fcuData.measurableRange.max / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    };

    getActiveGraphs = () => this.activeGraphs;
    getInactiveGraphs = () => this.inactiveGraphs;

    isAddingGraphs = () => this.isInactiveGraphsVisible;

    isFormValid = () => this.errorCount == 0;

    hasAlarmValidationErrors = () =>
        this.isViewInit && this.isInit && this.doAlarmFormsHaveErrors();

    getAlarmValidatorVeryLow = () => this.alarmValidators.veryLow;
    getAlarmValidatorLow = () => this.alarmValidators.low;
    getAlarmValidatorHigh = () => this.alarmValidators.high;
    getAlarmValidatorVeryHigh = () => this.alarmValidators.veryHigh;
    getAlarmValidatorFeedback = () => this.alarmValidators.feedback;
    getAlarmValidatorSensorFail = () => this.alarmValidators.sensorFail;
    getAlarmValidatorAlarmSetting = () => this.alarmValidators.alarmSetting;

    getDisabledAlarmFormFields = () => this.disabledAlarmFormFields;
    getDisabledFeedbackAlarmFormFields = () => this.disabledFeedbackAlarmFormFields;

    getMimics = () => this.mimics;

    getNotificationVisible() {
        return this.notification.getVisible();
    }
}
